import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Providers } from './providers';
import { AppShell } from './AppShell';
import { ErrorBoundary } from '@/lib/ui/ErrorBoundary';
import { useAuth } from '@/lib/auth/useAuth';
import { LoginPage } from '@/features/auth/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from '@/features/auth/ForgotResetPages';
import { AccountPage } from '@/features/auth/AccountPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { PagesListPage } from '@/features/pages/PagesListPage';
import { PageEditorPage } from '@/features/pages/PageEditorPage';
import { FamiliesListPage } from '@/features/families/FamiliesListPage';
import { FamilyEditorPage } from '@/features/families/FamilyEditorPage';
import { ContentListPage } from '@/features/content/ContentListPage';
import { ContentEditorPage } from '@/features/content/ContentEditorPage';
import { MediaLibraryPage } from '@/features/media/MediaLibraryPage';
import { LeadsPage } from '@/features/leads/LeadsPage';
import { AuditPage } from '@/features/audit/AuditPage';
import { UsersPage } from '@/features/users/UsersPage';
import { ApiTokensPage } from '@/features/apiTokens/ApiTokensPage';
import { SettingsPage } from '@/features/settings/SettingsPage';

export function App() {
  return (
    <Providers>
      {/* Outermost safety net: catches errors in the shell/auth chrome itself. */}
      <ErrorBoundary>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/forgot" element={<ForgotPasswordPage />} />
            <Route path="/reset" element={<ResetPasswordPage />} />
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <AppShell>
                    {/* Per-route boundary: a crash in one page is isolated to the content
                        area and clears automatically when the user navigates elsewhere. */}
                    <RouteErrorBoundary>
                      <Routes>
                        <Route path="/" element={<DashboardPage />} />
                        <Route path="/account" element={<AccountPage />} />
                        <Route path="/pages" element={<PagesListPage />} />
                        <Route path="/pages/:id" element={<PageEditorPage />} />
                        <Route path="/families" element={<FamiliesListPage />} />
                        <Route path="/families/:id" element={<FamilyEditorPage />} />
                        <Route path="/content/:collection" element={<ContentListPage />} />
                        <Route path="/content/:collection/:id" element={<ContentEditorPage />} />
                        <Route path="/media" element={<MediaLibraryPage />} />
                        <Route path="/leads/:type" element={<LeadsPage />} />
                        <Route path="/audit" element={<AuditPage />} />
                        <Route path="/users" element={<UsersPage />} />
                        <Route path="/tokens" element={<ApiTokensPage />} />
                        <Route path="/settings/*" element={<SettingsPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </RouteErrorBoundary>
                  </AppShell>
                </RequireAuth>
              }
            />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </Providers>
  );
}

/** Route-scoped error boundary that resets whenever the path changes. */
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

/** Auth guard: shows a splash while bootstrapping, redirects to login preserving deep-link. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { status, setPendingDeepLink } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas-sand">
        <span
          aria-label={t('common.loading')}
          className="h-[28px] w-[28px] animate-spin rounded-pill border-2 border-accent-blue border-e-transparent"
        />
      </div>
    );
  }
  if (status === 'unauthenticated') {
    const deepLink = location.pathname + location.search;
    if (deepLink !== '/') setPendingDeepLink(deepLink);
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}
